import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-12">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative w-full max-w-md rounded-3xl border border-[#1C2A38]/10 bg-[var(--surface)] p-8 shadow-2xl" data-testid="login-card">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-6 font-display text-2xl font-800 text-[#1C2A38]">Welcome back</h1>
          <p className="mt-1.5 text-sm text-[#5b6878]">Sign in to your ROSA account</p>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="login-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#3c4a5a]">Email</Label>
            <Input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" placeholder="you@company.com"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]"
            />
          </div>
          <div>
            <Label className="text-[#3c4a5a]">Password</Label>
            <Input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password" placeholder="••••••••"
              className="mt-1.5 border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]"
            />
          </div>
          <Button type="submit" disabled={busy} data-testid="login-submit" className="h-11 w-full bg-[var(--teal)] font-600 text-[#063b34] hover:bg-[#1ee9cf]">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-[#5b6878]">
          No account?{" "}
          <Link to="/register" className="font-600 text-[var(--teal-deep)] hover:underline">Create one</Link>
        </p>
      </div>
    </div>
  );
}
